import { Controller } from "@hotwired/stimulus";
import { AnnouncementConfig } from "../announcement_config";

const THEMES = {
  solid_dark: { wrapper: "bg-gray-900", text: "text-white" },
  solid_white: { wrapper: "bg-gray-100 dark:bg-gray-200", text: "text-gray-900" },
  violet: { wrapper: "bg-violet-600", text: "text-white" },
  blue: { wrapper: "bg-blue-600", text: "text-white" },
  emerald: { wrapper: "bg-emerald-600", text: "text-white" },
  amber: { wrapper: "bg-amber-500", text: "text-white" },
  rose: { wrapper: "bg-rose-500", text: "text-white" },
  indigo: { wrapper: "bg-indigo-600", text: "text-white" },
  slate: { wrapper: "bg-slate-600", text: "text-white" },
  brand_gradient: { wrapper: "bg-gradient-to-br from-primary-500 to-primary-700", text: "text-white" },
  warm_gradient: { wrapper: "bg-gradient-to-br from-amber-400 to-orange-600", text: "text-white" },
  cool_gradient: { wrapper: "bg-gradient-to-br from-blue-500 to-violet-600", text: "text-white" },
  sunset_gradient: { wrapper: "bg-gradient-to-br from-orange-500 to-rose-500", text: "text-white" },
  ocean_gradient: { wrapper: "bg-gradient-to-br from-cyan-500 to-blue-600", text: "text-white" },
};

const ALL_TEXT_CLASSES = ["text-white", "text-gray-900"];
const MIRROR_BASE = "text-center whitespace-pre-wrap break-words w-full max-w-[90%] font-extrabold";
const LINE_HEIGHT_TIGHT = "1.2";

export default class extends Controller {
  static targets = [
    "body",
    "form",
    "postButton",
    "postTypeInput",
    "taggedChips",
    "tagPeopleBtn",
    "tagPeopleSelector",
    "tagPeopleFrame",
    "announcementBtn",
    "announcementBadge",
    "photoBtn",
    "photoInput",
    "photoPreviews",
    "photoPreviewImage",
    "addToPostBar",
    "limitHint",
    "themeSwatches",
    "themeSwatchRow",
    "bodyWrapper",
    "authorRow",
    "regularBody",
    "announcementCard",
    "announcementCardInner",
    "announcementContentArea",
    "announcementEditor",
    "announcementMirror",
    "announcementMirrorText",
    "linesCharsCounter",
    "linesCount",
    "charsCount",
    "pollSection",
    "pollOption",
    "pollBtn",
  ];
  static values = { memberPickerUrl: String, memberPickerInlineUrl: String };

  connect() {
    this.selectedMembers = [];
    this.selectedPhotoFiles = [];
    this.announcement = false;
    this.pollActive = false;
    this.announcementTheme = "violet";
    this.announcementFontBucket = "Medium";
    this.boundHandleMemberSave = this.handleMemberSave.bind(this);
    this.boundHandleMemberPickerClose = this.closeTagPeople.bind(this);
    document.addEventListener("member-picker:save", this.boundHandleMemberSave);
    document.addEventListener("member-picker:close", this.boundHandleMemberPickerClose);
    this.updatePostButton();
  }

  disconnect() {
    document.removeEventListener("member-picker:save", this.boundHandleMemberSave);
    document.removeEventListener("member-picker:close", this.boundHandleMemberPickerClose);
  }

  onBodyInput() {
    if (!this.announcement) this.updatePostButton();
  }

  onBodyKeydown() {}

  openTagPeople() {
    if (this.announcement) return;
    if (!this.hasTagPeopleSelectorTarget) return;
    const url = this.memberPickerInlineUrlValue || this.memberPickerUrlValue;
    if (this.hasTagPeopleFrameTarget && url) this.tagPeopleFrameTarget.src = url;
    this.tagPeopleSelectorTarget.classList.remove("hidden");
    this.tagPeopleBtnTarget?.classList.add("bg-blue-50", "dark:bg-blue-900/20");
  }

  closeTagPeople() {
    if (!this.hasTagPeopleSelectorTarget) return;
    this.tagPeopleSelectorTarget.classList.add("hidden");
    this.tagPeopleBtnTarget?.classList.remove("bg-blue-50", "dark:bg-blue-900/20");
    if (this.hasBodyTarget && !this.bodyTarget.disabled) {
      this.bodyTarget.focus();
    }
  }

  closeTagPeopleOnOutsideClick(event) {
    if (!this.hasTagPeopleSelectorTarget || this.tagPeopleSelectorTarget.classList.contains("hidden")) return;
    if (this.tagPeopleSelectorTarget.contains(event.target)) return;
    if (this.tagPeopleBtnTarget?.contains(event.target)) return;
    this.closeTagPeople();
  }

  switchToPost() {
    if (this.announcement) {
      this.announcement = false;
      this.hideLimitHint();
      this.announcementBtnTarget?.classList.remove("bg-primary-100", "dark:bg-primary-900/30", "text-primary-600", "dark:text-primary-400");
      this.exitAnnouncementMode();
    }
    if (this.hasPostTypeInputTarget) this.postTypeInputTarget.value = "regular";
    this.addMetadataInputs();
  }

  switchToAnnouncement() {
    if (this.announcement) return;
    const text = this.bodyTarget?.value?.trim() ?? "";
    const result = AnnouncementConfig.compute(text);
    if (!result.valid) {
      this.showLimitHint();
      return;
    }
    this.hideLimitHint();
    this.announcement = true;
    this.announcementBtnTarget?.classList.add("bg-primary-100", "dark:bg-primary-900/30");
    this.announcementBtnTarget?.classList.add("text-primary-600", "dark:text-primary-400");
    this.enterAnnouncementMode();
    this.addMetadataInputs();
  }

  showLimitHint() {
    if (this.hasLimitHintTarget) {
      this.limitHintTarget.textContent = AnnouncementConfig.limitHint();
      this.limitHintTarget.classList.remove("hidden");
    }
  }

  hideLimitHint() {
    this.limitHintTarget?.classList.add("hidden");
  }

  toggleAnnouncement() {
    if (this.announcement) {
      this.switchToPost();
    } else {
      this.switchToAnnouncement();
    }
  }

  togglePoll() {
    if (this.announcement) return;
    this.pollActive = !this.pollActive;
    if (this.hasPollSectionTarget) {
      this.pollSectionTarget.classList.toggle("hidden", !this.pollActive);
    }
    if (this.hasPostTypeInputTarget) {
      this.postTypeInputTarget.value = this.pollActive ? "poll" : "regular";
    }
    if (this.hasPollBtnTarget) {
      this.pollBtnTarget.classList.toggle("bg-emerald-50", this.pollActive);
      this.pollBtnTarget.classList.toggle("dark:bg-emerald-900/20", this.pollActive);
      this.pollBtnTarget.classList.toggle("text-emerald-500", this.pollActive);
      this.pollBtnTarget.classList.toggle("dark:text-emerald-400", this.pollActive);
    }
    // Update poll option required attributes
    if (this.hasPollOptionTarget) {
      this.pollOptionTargets.forEach((input, i) => {
        if (i < 2) input.required = this.pollActive;
      });
    }
  }

  dismissAnnouncement() {
    this.switchToPost();
  }

  triggerPhotoInput() {
    if (this.hasPhotoInputTarget) this.photoInputTarget.click();
  }

  onPhotoSelect(event) {
    const files = Array.from(event.target.files || []);
    if (files.length === 0) return;
    this.selectedPhotoFiles = files.slice(0, 10);
    this.renderPhotoPreviews();
  }

  renderPhotoPreviews() {
    if (!this.hasPhotoPreviewsTarget || this.selectedPhotoFiles.length === 0) return;
    this.photoPreviewsTarget.classList.remove("hidden");
    const file = this.selectedPhotoFiles[0];
    if (this.hasPhotoPreviewImageTarget && file) {
      this.photoPreviewImageTarget.src = URL.createObjectURL(file);
      this.photoPreviewImageTarget.alt = "Selected image";
      this.photoPreviewImageTarget.classList.remove("hidden");
    }
  }

  changePhoto() {
    this.triggerPhotoInput();
  }

  removeAllPhotos() {
    this.selectedPhotoFiles = [];
    if (this.hasPhotoInputTarget) this.photoInputTarget.value = "";
    if (this.hasPhotoPreviewsTarget) this.photoPreviewsTarget.classList.add("hidden");
    if (this.hasPhotoPreviewImageTarget) {
      this.photoPreviewImageTarget.src = "";
      this.photoPreviewImageTarget.classList.add("hidden");
    }
  }

  updatePhotoButtonState() {
    if (!this.hasPhotoBtnTarget) return;
    const disable = this.announcement;
    this.photoBtnTarget.disabled = disable;
  }

  enterAnnouncementMode() {
    this.addToPostBarTarget?.classList.add("hidden");
    this.taggedChipsTarget?.classList.add("hidden");
    this.announcementBadgeTarget?.classList.add("hidden");
    this.authorRowTarget?.classList.add("hidden");
    this.regularBodyTarget?.classList.add("hidden");
    this.themeSwatchesTarget?.classList.remove("hidden");
    this.closeTagPeople();

    const text = this.enforceAnnouncementLimits(this.bodyTarget.value);
    this.bodyTarget.value = text;
    this.bodyTarget.classList.add("sr-only", "absolute", "h-0", "overflow-hidden", "opacity-0", "pointer-events-none");

    this.announcementEditorTarget.textContent = text;
    this.announcementCardTarget?.classList.remove("hidden");
    this.linesCharsCounterTarget?.classList.remove("hidden");
    this.applyAnnouncementCardStyles();
    this.updateLinesCharsCounter();
    this.applyBracketAndFitCheck();

    this.tagPeopleBtnTarget?.setAttribute("aria-disabled", "true");
    this.tagPeopleBtnTarget?.classList.add("opacity-50", "cursor-not-allowed");
    this.tagPeopleBtnTarget?.classList.remove("hover:bg-gray-100/80", "dark:hover:bg-gray-700/50", "hover:text-blue-500");
    this.updatePhotoButtonState();
  }

  enforceAnnouncementLimits(str) {
    let text = (str ?? "").slice(0, AnnouncementConfig.MAX_CHARS);
    const lines = text.split("\n");
    if (lines.length > AnnouncementConfig.MAX_LINES) {
      text = lines.slice(0, AnnouncementConfig.MAX_LINES).join("\n");
    }
    return text;
  }

  getEditorText() {
    // Use innerText so <br>/<div> line breaks become \n (textContent loses them)
    const raw = this.announcementEditorTarget?.innerText ?? "";
    return raw.slice(0, AnnouncementConfig.MAX_CHARS);
  }

  onAnnouncementEditorInput() {
    const raw = this.announcementEditorTarget.innerText ?? "";
    const limited = this.enforceAnnouncementLimits(raw);
    if (limited !== raw) {
      this.announcementEditorTarget.textContent = limited;
      this.moveCaretToEnd();
    }
    this.bodyTarget.value = limited;

    const result = AnnouncementConfig.compute(limited);
    if (!result.valid) {
      this.fallbackToPost();
      return;
    }

    this.updateLinesCharsCounter();
    this.applyBracketAndFitCheck();
    this.updatePostButton();
  }

  moveCaretToEnd() {
    const el = this.announcementEditorTarget;
    const range = document.createRange();
    range.selectNodeContents(el);
    range.collapse(false);
    const sel = window.getSelection();
    sel?.removeAllRanges();
    sel?.addRange(range);
  }

  onAnnouncementKeydown(event) {
    const text = this.getEditorText();
    if (event.key === "Enter") {
      const lines = AnnouncementConfig.countLines(text);
      if (lines >= AnnouncementConfig.MAX_LINES) {
        event.preventDefault();
        this.shakeCard();
        return;
      }
    }
    if (text.length >= AnnouncementConfig.MAX_CHARS && !["Backspace", "Delete", "ArrowLeft", "ArrowRight", "Tab"].includes(event.key)) {
      event.preventDefault();
    }
  }

  onAnnouncementPaste(event) {
    event.preventDefault();
    const pasted = (event.clipboardData?.getData("text/plain") || "").slice(0, AnnouncementConfig.MAX_CHARS);
    const limited = this.enforceAnnouncementLimits(pasted);
    const current = this.getEditorText();
    const sel = window.getSelection();
    if (!sel.rangeCount) return;
    const range = sel.getRangeAt(0);
    const startContainer = range.startContainer;
    if (!this.announcementEditorTarget.contains(startContainer)) return;

    const pre = this.announcementEditorTarget.textContent?.slice(0, this.getCaretOffset()) || "";
    const post = this.getEditorText().slice(this.getCaretOffset()) || "";
    const newText = this.enforceAnnouncementLimits(pre + limited + post);
    this.announcementEditorTarget.textContent = newText;
    this.bodyTarget.value = newText;
    this.moveCaretToEnd();
    this.announcementEditorTarget.dispatchEvent(new Event("input", { bubbles: true }));
  }

  getCaretOffset() {
    const sel = window.getSelection();
    if (!sel.rangeCount) return 0;
    const range = sel.getRangeAt(0);
    const pre = range.cloneRange();
    pre.selectNodeContents(this.announcementEditorTarget);
    pre.setEnd(range.endContainer, range.endOffset);
    return pre.toString().length;
  }

  exitAnnouncementMode() {
    this.bodyTarget.value = this.getEditorText();
    this.bodyTarget.classList.remove("sr-only", "absolute", "h-0", "overflow-hidden", "opacity-0", "pointer-events-none");

    this.removeAnnouncementCardStyles();
    this.regularBodyTarget?.classList.remove("hidden");
    this.announcementCardTarget?.classList.add("hidden");
    this.addToPostBarTarget?.classList.remove("hidden");
    this.announcementBadgeTarget?.classList.add("hidden");
    this.authorRowTarget?.classList.remove("hidden");
    this.themeSwatchesTarget?.classList.add("hidden");
    this.linesCharsCounterTarget?.classList.add("hidden");

    this.tagPeopleBtnTarget?.removeAttribute("aria-disabled");
    this.tagPeopleBtnTarget?.classList.remove("opacity-50", "cursor-not-allowed");
    this.tagPeopleBtnTarget?.classList.add("hover:bg-gray-100/80", "dark:hover:bg-gray-700/50", "hover:text-blue-500");
    if (this.selectedMembers.length > 0) this.taggedChipsTarget?.classList.remove("hidden");

    this.updatePhotoButtonState();
    this.updatePostButton();
  }

  applyAnnouncementCardStyles() {
    if (!this.hasAnnouncementCardInnerTarget || !this.hasAnnouncementEditorTarget) return;
    const theme = THEMES[this.announcementTheme] || THEMES.violet;
    const allWrapper = Object.values(THEMES).flatMap((t) => t.wrapper.split(" "));
    this.announcementCardInnerTarget.classList.remove(...allWrapper);
    this.announcementCardInnerTarget.classList.add("rounded-2xl", ...theme.wrapper.split(" "));
    this.announcementCardInnerTarget.dataset.placeholderTheme = theme.text.includes("gray-900") ? "light" : "dark";
    this.announcementEditorTarget.classList.remove(...ALL_TEXT_CLASSES);
    this.announcementEditorTarget.style.fontSize = "";
    this.announcementEditorTarget.style.lineHeight = "";
    this.announcementEditorTarget.classList.add("font-extrabold", "leading-[1.2]", ...theme.text.split(" "));
    this.applyBracketAndFitCheck();
  }

  removeAnnouncementCardStyles() {
    if (!this.hasAnnouncementCardInnerTarget || !this.hasAnnouncementEditorTarget) return;
    const allWrapper = Object.values(THEMES).flatMap((t) => t.wrapper.split(" "));
    this.announcementCardInnerTarget.classList.remove("rounded-2xl", ...allWrapper);
    delete this.announcementCardInnerTarget.dataset.placeholderTheme;
    this.announcementEditorTarget.classList.remove("font-extrabold", "leading-[1.2]", ...ALL_TEXT_CLASSES);
    this.announcementEditorTarget.style.fontSize = "";
    this.announcementEditorTarget.style.lineHeight = "";
  }

  applyBracketAndFitCheck() {
    if (!this.announcement || !this.hasAnnouncementEditorTarget || !this.hasAnnouncementContentAreaTarget || !this.hasAnnouncementMirrorTextTarget) return;
    requestAnimationFrame(() => {
      if (!this.announcement) return;
      const text = this.getEditorText().trim() || " ";
      const result = AnnouncementConfig.compute(text);
      if (!result.valid) {
        this.fallbackToPost();
        return;
      }

      const contentArea = this.announcementContentAreaTarget;
      const mirrorText = this.announcementMirrorTextTarget;
      const contentHeight = contentArea.clientHeight;
      const contentWidth = contentArea.clientWidth;
      if (contentHeight === 0 || contentWidth === 0) return;

      // When text has \n, mirror uses paragraph layout (flex + mt-2) to match feed
      const paragraphs = text.split("\n");
      const hasNewlines = paragraphs.length > 1;

      if (hasNewlines) {
        mirrorText.innerHTML = "";
        mirrorText.className = "flex flex-col items-center w-full max-w-[90%] font-extrabold text-center break-words";
        mirrorText.style.fontSize = `${result.fontSize}px`;
        mirrorText.style.lineHeight = LINE_HEIGHT_TIGHT;
        paragraphs.forEach((para, i) => {
          const span = document.createElement("span");
          span.className = `block w-full ${i > 0 ? "mt-2" : ""}`;
          span.textContent = para;
          mirrorText.appendChild(span);
        });
      } else {
        mirrorText.innerHTML = "";
        mirrorText.textContent = text;
        mirrorText.className = MIRROR_BASE;
        mirrorText.style.fontSize = `${result.fontSize}px`;
        mirrorText.style.lineHeight = LINE_HEIGHT_TIGHT;
      }

      const mirrorHeight = mirrorText.offsetHeight;
      const mirrorWidth = mirrorText.offsetWidth;

      if (mirrorHeight <= contentHeight && mirrorWidth <= contentWidth) {
        this.announcementEditorTarget.style.fontSize = `${result.fontSize}px`;
        this.announcementEditorTarget.style.lineHeight = LINE_HEIGHT_TIGHT;
        this.announcementFontBucket = result.bracket;
        return;
      }

      if (result.bracket === "Small") {
        this.fallbackToPost();
      } else {
        const fallbackBracket = result.bracket === "Large" ? "Medium" : "Small";
        const fallbackPx = AnnouncementConfig.FONT_SIZES[fallbackBracket];
        mirrorText.style.fontSize = `${fallbackPx}px`;
        const mh = mirrorText.offsetHeight;
        const mw = mirrorText.offsetWidth;
        if (mh <= contentHeight && mw <= contentWidth) {
          this.announcementEditorTarget.style.fontSize = `${fallbackPx}px`;
          this.announcementEditorTarget.style.lineHeight = LINE_HEIGHT_TIGHT;
          this.announcementFontBucket = fallbackBracket;
        } else if (fallbackBracket === "Small") {
          this.fallbackToPost();
        } else {
          const smallPx = AnnouncementConfig.FONT_SIZES.Small;
          mirrorText.style.fontSize = `${smallPx}px`;
          if (mirrorText.offsetHeight <= contentHeight && mirrorText.offsetWidth <= contentWidth) {
            this.announcementEditorTarget.style.fontSize = `${smallPx}px`;
            this.announcementEditorTarget.style.lineHeight = LINE_HEIGHT_TIGHT;
            this.announcementFontBucket = "Small";
          } else {
            this.fallbackToPost();
          }
        }
      }
    });
  }

  fallbackToPost() {
    if (!this.announcement) return;
    this.announcement = false;
    this.showLimitHint();
    this.announcementBtnTarget?.classList.remove("bg-primary-100", "dark:bg-primary-900/30", "text-primary-600", "dark:text-primary-400");
    this.exitAnnouncementMode();
    this.addMetadataInputs();
  }

  shakeCard() {
    const el = this.announcementCardInnerTarget;
    if (!el) return;
    el.style.animation = "shake 0.4s ease-in-out";
    setTimeout(() => { el.style.animation = ""; }, 400);
  }

  updateLinesCharsCounter() {
    if (!this.hasLinesCharsCounterTarget || !this.hasLinesCountTarget || !this.hasCharsCountTarget) return;
    const text = this.getEditorText?.() ?? this.bodyTarget?.value ?? "";
    const lines = AnnouncementConfig.countLines(text);
    const chars = AnnouncementConfig.countChars(text);
    this.linesCountTarget.textContent = lines;
    this.charsCountTarget.textContent = chars;
  }

  selectTheme(event) {
    const theme = event.currentTarget.dataset.theme;
    if (!theme || !THEMES[theme]) return;
    this.announcementTheme = theme;
    this.themeSwatchRowTarget?.querySelectorAll("button[data-theme]").forEach((btn) => {
      const sel = btn.dataset.theme === theme;
      btn.classList.toggle("ring-2", sel);
      btn.classList.toggle("ring-primary-500", sel);
      btn.classList.toggle("ring-offset-2", sel);
    });
    if (this.announcement) this.applyAnnouncementCardStyles();
    this.addMetadataInputs();
  }

  handleMemberSave(event) {
    this.selectedMembers = event.detail?.members || [];
    this.renderTaggedChips();
    this.addMetadataInputs();
    this.updatePostButton();
  }

  renderTaggedChips() {
    if (!this.hasTaggedChipsTarget) return;
    if (this.selectedMembers.length === 0) {
      this.taggedChipsTarget.innerHTML = "";
      this.taggedChipsTarget.classList.add("hidden");
      return;
    }
    if (this.announcement) return;
    this.taggedChipsTarget.classList.remove("hidden");
    const chipsHtml = this.selectedMembers
      .map(
        (m) => `
        <span data-member-id="${m.id}" class="inline-flex items-center gap-1.5 pl-1 pr-2 py-1 rounded-full bg-gray-100 dark:bg-gray-700 text-sm text-gray-800 dark:text-gray-200">
          <span class="w-6 h-6 rounded-full bg-primary-100 dark:bg-primary-900/50 flex items-center justify-center text-xs font-medium">${(m.name || "?")[0]}</span>
          ${m.name || "?"}
          <button type="button" data-action="click->community-composer-modal#removeTag" data-member-id="${m.id}" class="p-0.5 rounded-full hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors" aria-label="Remove">
            <svg class="size-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/></svg>
          </button>
        </span>
      `
      )
      .join("");
    this.taggedChipsTarget.innerHTML = `<div class="flex flex-wrap items-center gap-x-2 gap-y-3">${chipsHtml}</div>`;
  }

  removeTag(event) {
    const id = parseInt(event.currentTarget.dataset.memberId, 10);
    this.selectedMembers = this.selectedMembers.filter((m) => m.id !== id);
    this.renderTaggedChips();
    this.addMetadataInputs();
    this.updatePostButton();
  }

  addMetadataInputs() {
    if (!this.hasFormTarget) return;
    this.formTarget.querySelectorAll('[name="post[announcement]"], [name="post[announcement_theme]"], [name="post[announcement_style]"], [name="post[announcement_aspect]"], [name="post[announcement_font_bucket]"]').forEach((el) => el.remove());

    if (this.announcement) {
      this.bodyTarget.value = this.getEditorText();
      for (const [name, value] of [
        ["post[announcement]", "1"],
        ["post[announcement_theme]", this.announcementTheme],
        ["post[announcement_style]", "large_text"],
        ["post[announcement_aspect]", "4:3"],
        ["post[announcement_font_bucket]", this.announcementFontBucket],
      ]) {
        const inp = document.createElement("input");
        inp.type = "hidden";
        inp.name = name;
        inp.value = value;
        this.formTarget.appendChild(inp);
      }
    }

    this.formTarget.querySelectorAll('[name="post[tagged_member_ids][]"]').forEach((el) => el.remove());
    this.selectedMembers.forEach((m) => {
      const inp = document.createElement("input");
      inp.type = "hidden";
      inp.name = "post[tagged_member_ids][]";
      inp.value = m.id;
      this.formTarget.appendChild(inp);
    });
  }

  updatePostButton() {
    if (!this.hasPostButtonTarget) return;
    let hasContent = false;
    if (this.announcement) {
      const text = this.getEditorText?.() ?? "";
      hasContent = text.trim().length > 0;
    } else {
      const text = this.bodyTarget?.value ?? "";
      hasContent = text.trim().length > 0;
    }
    this.postButtonTarget.disabled = !hasContent;
  }

  onFormSubmit(event) {
    if (!this.announcement) return;
    this.bodyTarget.value = this.getEditorText();
    const result = AnnouncementConfig.compute(this.bodyTarget.value);
    if (!result.valid) {
      event.preventDefault();
      this.fallbackToPost();
    }
  }
}
